import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { motion, useDragControls, useMotionValue } from "motion/react";
import { X, Palette, Search } from "lucide-react";
import cadviewer from "cadviewer";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { useSpaceToolsStore } from "../SpaceTools/stores/spaceToolsStore";
import EventSystem from "../../events/EventSystem";
import $ from "jquery";

// Position styles mapping
const positionStyles: Record<string, string> = {
  "bottom-left": "absolute top-0 left-0 z-[9999]",
  "bottom-right": "absolute top-0 right-0 z-[9999]",
  "top-right": "absolute top-0 right-0 z-[9999]",
};

interface LayerItem {
  id: string;
  name: string;
  state: "On" | "Off";
  color?: string; 
  hasCustomStyle: boolean;
  scaleFactor: number;
}

interface LayerGroup {
  id: string;
  name: string;
  layerIds: string[];
}

export const SvgStylingPanel: React.FC = () => {
  const { config } = useConfig();
  const { currentCanvasInstance, currentDrawingPath, currentDrawingVersion } = useContext(CadviewerContext);
  const { activePanel, closePanel, selectedLayers, toggleLayerSelection, clearSelectedLayers } = useSpaceToolsStore();

  const isOpen = activePanel === "svgStyling";

  const [layers, setLayers] = useState<LayerItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortAscending, setSortAscending] = useState(true);
  const [isSearchingFromCanvas, setIsSearchingFromCanvas] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [includeFrozenLayers, setIncludeFrozenLayers] = useState(true);

  // Pop-up states
  const [activeSliderLayer, setActiveSliderLayer] = useState<string | null>(null);
  const [activeColorLayer, setActiveColorLayer] = useState<string | null>(null);
  const [tempColor, setTempColor] = useState<string>("#cccccc");

  // Multiselect states
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [multiSelectedLayers, setMultiSelectedLayers] = useState<string[]>([]);
  const [multiActiveSlider, setMultiActiveSlider] = useState(false);
  const [multiActiveColor, setMultiActiveColor] = useState(false);
  const [multiTempColor, setMultiTempColor] = useState<string>("#cccccc");
  const [multiScaleFactor, setMultiScaleFactor] = useState<number>(1);

  // Group states
  const [layerGroups, setLayerGroups] = useState<LayerGroup[]>([]);
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");

  const isMultiSelectModeRef = useRef(isMultiSelectMode);
  useEffect(() => { isMultiSelectModeRef.current = isMultiSelectMode; }, [isMultiSelectMode]);

  const layersRef = useRef(layers);
  useEffect(() => { layersRef.current = layers; }, [layers]);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Automatically turn off the filter if we have no layers selected
  useEffect(() => {
    if (selectedLayers.length === 0 && showOnlySelected) {
      setShowOnlySelected(false);
    }
  }, [selectedLayers.length, showOnlySelected]);

  // Fetch layers when panel opens
  useEffect(() => {
    if (isOpen) {
      try {
        const layerTable = cadviewer.cvjs_getLayerTable(currentCanvasInstance || "floorPlan");
        if (layerTable && layerTable.layers) {
          const parsedLayers = layerTable.layers.map((l: any, index: number) => {
            let currentState = "On";
            if (l.layerState === "Off" || l.status === "OFF" || l.status === "off") {
              currentState = "Off";
            }
            
            const rawName = String(l.layerName || l.layer || l.name || "").trim();
            const rawId = String(l.layer || l.name || l.layerName || "").trim();
            
            return {
              id: `${rawId}_${index}`, // Append index to ensure React keys are unique
              name: rawName,
              originalId: rawId, // Keep the original ID for CADViewer API calls
              state: currentState as "On" | "Off",
              color: l.colorHex || l.color || "#cccccc",
              hasCustomStyle: false,
              scaleFactor: 1, // Default factor
            };
          });
          
          setLayers(parsedLayers);
        }
      } catch (e) {
        console.error("Error fetching layer table:", e);
      }
    } else {
      // Clean up search mode if closed
      if (isSearchingFromCanvas && cadviewer.cvjs_processLayerHandles) {
        cadviewer.cvjs_processLayerHandles(false);
        setIsSearchingFromCanvas(false);
      }
      setActiveSliderLayer(null);
      setActiveColorLayer(null);
    }
  }, [isOpen, currentCanvasInstance]);

  const handleToggleLayer = (layerId: string, originalId: string, currentState: "On" | "Off") => {
    const newState = currentState === "On" ? "Off" : "On";
    
    if (newState === "On") {
      cadviewer.cvjs_LayerOn(originalId);
    } else {
      cadviewer.cvjs_LayerOff(originalId);
    }

    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, state: newState } : l))
    );
  };

  const handleAllLayersOn = () => {
    layers.forEach((l) => {
      if (l.state === "Off") cadviewer.cvjs_LayerOn((l as any).originalId);
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "On" })));
  };

  const handleAllLayersOff = () => {
    layers.forEach((l) => {
      if (l.state === "On") cadviewer.cvjs_LayerOff((l as any).originalId);
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "Off" })));
  };

  const handleSwapLayers = () => {
    layers.forEach((l) => {
      if (l.state === "On") {
        cadviewer.cvjs_LayerOff((l as any).originalId);
      } else {
        cadviewer.cvjs_LayerOn((l as any).originalId);
      }
    });
    setLayers((prev) =>
      prev.map((l) => ({ ...l, state: l.state === "On" ? "Off" : "On" }))
    );
  };

  const handleSearchFromFloorplan = () => {
    if (!cadviewer.cvjs_processLayerHandles) {
      console.warn("cvjs_processLayerHandles is not available.");
      return;
    }

    if (isSearchingFromCanvas) {
      cadviewer.cvjs_processLayerHandles(false);
      setIsSearchingFromCanvas(false);
    } else {
      setIsSearchingFromCanvas(true);
      cadviewer.cvjs_processLayerHandles(true, (layerId: string, layerName: string) => {
        setIsSearchingFromCanvas(false);
        if (isMultiSelectModeRef.current) {
          const foundLayer = layersRef.current.find(l => l.name === layerName);
          if (foundLayer) {
            setMultiSelectedLayers(prev => {
              if (!prev.includes(foundLayer.id)) {
                return [...prev, foundLayer.id];
              }
              return prev;
            });
          }
        } else {
          setSearchQuery(layerName);
          setShowOnlySelected(false);
        }
      });
    }
  };

  const handleColorChange = (layerId: string, layerName: string, newColor: string) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, color: newColor, hasCustomStyle: true } : l))
    );
    // Apply color using CADViewer API
    cadviewer.cvjs_colorSingleLayer(currentCanvasInstance || "floorPlan", newColor, layerName);
  };

  const handleLineweightChange = (layerId: string, layerName: string, value: number) => {
    const scaleFactor = value / 10; // e.g. value 10 = factor 1.0, value 20 = 2.0
    
    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, scaleFactor: scaleFactor, hasCustomStyle: true } : l))
    );

    // Apply lineweight natively using jQuery
    $(`g[cvjs\\:layername="${layerName}"]`).find('path[id^="cv_"]').each(function () {
        var $path = $(this);
        if (/^cv_\d+$/.test($path.attr('id') || '')) {
            $path.attr('style', `stroke-width: calc(var(--tmsCalcMinLineWeight)*${scaleFactor})`);
        }
    });
  };

  const handleMultiColorChange = (newColor: string) => {
    const layersToUpdate = layers.filter(l => multiSelectedLayers.includes(l.id));
    
    layersToUpdate.forEach(l => {
      cadviewer.cvjs_colorSingleLayer(currentCanvasInstance || "floorPlan", newColor, l.name);
    });

    setLayers((prev) =>
      prev.map((l) => multiSelectedLayers.includes(l.id) ? { ...l, color: newColor, hasCustomStyle: true } : l)
    );
  };

  const handleMultiLineweightChange = (value: number) => {
    const scaleFactor = value / 10;
    const layersToUpdate = layers.filter(l => multiSelectedLayers.includes(l.id));

    layersToUpdate.forEach(l => {
      $(`g[cvjs\\:layername="${l.name}"]`).find('path[id^="cv_"]').each(function () {
          var $path = $(this);
          if (/^cv_\d+$/.test($path.attr('id') || '')) {
              $path.attr('style', `stroke-width: calc(var(--tmsCalcMinLineWeight)*${scaleFactor})`);
          }
      });
    });

    setLayers((prev) =>
      prev.map((l) => multiSelectedLayers.includes(l.id) ? { ...l, scaleFactor: scaleFactor, hasCustomStyle: true } : l)
    );
  };

  const toggleCustomStyle = (layerId: string) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, hasCustomStyle: !l.hasCustomStyle } : l))
    );
  };

  const filteredAndSortedLayers = useMemo(() => {
    let result = [...layers];
    
    if (showOnlySelected) {
      result = result.filter((layer) => selectedLayers.includes(layer.id));
    }
    
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((l) => l.name.toLowerCase().includes(lowerQuery));
    }

    result.sort((a, b) => {
      const compare = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      return sortAscending ? compare : -compare;
    });

    return result;
  }, [layers, searchQuery, sortAscending, showOnlySelected, selectedLayers]);

  const positionClass = positionStyles[config.spaceSelectionPosition] || positionStyles["bottom-left"];

  if (!isOpen) return null;

  return (
    <div className={positionClass}>
      <motion.div
        drag
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className="bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden w-[450px] pointer-events-auto flex flex-col relative"
      >
        {/* Header */}
        <div
          className="px-3 py-2 text-white cursor-move flex items-center justify-between"
          style={{ background: 'linear-gradient(to right, #0f3557, #0a2a42)', userSelect: 'none' }}
          onPointerDown={(e) => dragControls.start(e)}
        >
          <h3 className="text-xs font-bold flex items-center gap-2">
            <Palette className="w-4 h-4" />
            CADViewer - SVG Styling
          </h3>
          <button
            onClick={closePanel}
            className="p-1 rounded transition-colors hover:bg-[#0a2a42]"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Top Controls */}
        <div className="p-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSearchFromFloorplan}
              className={`flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs shadow-sm cursor-pointer ${
                isSearchingFromCanvas 
                  ? "border-amber-600 bg-amber-50 text-amber-700 hover:bg-amber-100" 
                  : "border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              }`}
            >
              <Search className="w-3.5 h-3.5" />
              <span className="whitespace-nowrap">{isSearchingFromCanvas ? "Click canvas..." : "Search from floorplan"}</span>
            </button>
            
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs shadow-sm cursor-pointer border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
              >
                <X className="w-3.5 h-3.5" />
                <span className="whitespace-nowrap">Clear Search</span>
              </button>
            )}
          </div>
          
          <button
            onClick={() => EventSystem.publish("modal.save_svg.open", {})}
            className="flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs shadow-sm cursor-pointer border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            <span className="whitespace-nowrap">Save as SVG</span>
          </button>
        </div>

        {/* Search & Sort Controls */}
        <div className="p-3 border-b border-slate-200 flex flex-col gap-2">
          {layerGroups.length > 0 && (
            <div className="flex gap-2">
              <select 
                value={selectedGroupId}
                onChange={(e) => {
                  const groupId = e.target.value;
                  setSelectedGroupId(groupId);
                  if (groupId) {
                    const group = layerGroups.find(g => g.id === groupId);
                    if (group) {
                      setIsMultiSelectMode(true);
                      setMultiSelectedLayers(group.layerIds);
                      // Turn on "Show only selected" so they can easily see which layers the group consists of
                      setShowOnlySelected(true);
                    }
                  } else {
                    // if they deselect the group, maybe leave the selection as is or clear it?
                  }
                }}
                className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none bg-white text-slate-700"
              >
                <option value="">Saved layer groups...</option>
                {layerGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name} ({g.layerIds.length} layers)</option>
                ))}
              </select>
            </div>
          )}
          
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search layers"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={() => setSortAscending(!sortAscending)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 whitespace-nowrap"
            >
              Sort by Layer {sortAscending ? "▲" : "▼"}
            </button>
            <button
              onClick={() => {
                const newMode = !isMultiSelectMode;
                setIsMultiSelectMode(newMode);
                if (!newMode) {
                  setMultiSelectedLayers([]);
                  setSelectedGroupId("");
                }
              }}
              className={`px-3 py-1.5 text-sm border rounded-md whitespace-nowrap transition-colors ${
                isMultiSelectMode 
                  ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800" 
                  : "border-slate-300 hover:bg-slate-50 text-slate-700"
              }`}
            >
              Multiselect
            </button>
          </div>

          {isMultiSelectMode && (
            <div className="flex gap-2 items-center bg-fuchsia-50 border border-fuchsia-100 p-2 rounded-md overflow-x-auto">
              <span className="text-xs font-semibold text-fuchsia-800 whitespace-nowrap">
                {multiSelectedLayers.length} selected
              </span>
              
              <button 
                onClick={() => setMultiActiveSlider(true)}
                className="p-1.5 rounded text-slate-600 hover:text-indigo-600 hover:bg-white bg-white/50 transition-colors border border-slate-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                title="Adjust Lineweight for selected"
                disabled={multiSelectedLayers.length === 0}
              >
                <img src="/assets/cadviewer/app/images/singleIcons/svg/light-skin/adjustlayerlineweight.svg" alt="" className="w-4 h-4 opacity-80" />
              </button>
              
              <button 
                onClick={() => setMultiActiveColor(true)}
                className="p-1.5 rounded text-slate-600 hover:text-indigo-600 hover:bg-white bg-white/50 transition-colors border border-slate-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                title="Edit Color for selected"
                disabled={multiSelectedLayers.length === 0}
              >
                <img src="/assets/cadviewer/app/images/singleIcons/svg/light-skin/layercolor.svg" alt="" className="w-4 h-4 opacity-80" />
              </button>

              <div className="flex-1 min-w-[4px]"></div>
              
              {isSavingGroup ? (
                <div className="flex items-center gap-1 bg-white p-0.5 rounded border border-fuchsia-200 flex-shrink-0">
                  <input 
                    type="text" 
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Group name"
                    className="w-20 px-1 py-1 text-[11px] border-none focus:outline-none focus:ring-0 text-slate-700 bg-transparent"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newGroupName.trim() && multiSelectedLayers.length > 0) {
                        const newGroup = {
                          id: Date.now().toString(),
                          name: newGroupName.trim(),
                          layerIds: [...multiSelectedLayers]
                        };
                        setLayerGroups([...layerGroups, newGroup]);
                        setSelectedGroupId(newGroup.id);
                        setNewGroupName("");
                        setIsSavingGroup(false);
                      } else if (e.key === 'Escape') {
                        setIsSavingGroup(false);
                      }
                    }}
                  />
                  <button 
                    onClick={() => {
                      if (newGroupName.trim() && multiSelectedLayers.length > 0) {
                        const newGroup = {
                          id: Date.now().toString(),
                          name: newGroupName.trim(),
                          layerIds: [...multiSelectedLayers]
                        };
                        setLayerGroups([...layerGroups, newGroup]);
                        setSelectedGroupId(newGroup.id);
                        setNewGroupName("");
                        setIsSavingGroup(false);
                      }
                    }}
                    disabled={!newGroupName.trim()}
                    className="px-1.5 py-1 bg-fuchsia-600 text-white rounded text-[10px] font-medium disabled:opacity-50 hover:bg-fuchsia-700 transition-colors"
                  >
                    Save
                  </button>
                  <button 
                    onClick={() => setIsSavingGroup(false)} 
                    className="px-1 py-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  >
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsSavingGroup(true)}
                  disabled={multiSelectedLayers.length === 0}
                  className="px-2 py-1 bg-white border border-fuchsia-200 text-fuchsia-700 rounded shadow-sm text-xs font-medium hover:bg-fuchsia-50 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                >
                  Save Group
                </button>
              )}

              <button
                onClick={() => {
                  if (multiSelectedLayers.length === filteredAndSortedLayers.length) {
                    setMultiSelectedLayers([]);
                  } else {
                    setMultiSelectedLayers(filteredAndSortedLayers.map(l => l.id));
                  }
                }}
                className="px-2 py-1 bg-white border border-slate-300 rounded shadow-sm text-[11px] font-medium hover:bg-slate-50 text-slate-700 flex-shrink-0 whitespace-nowrap ml-1"
              >
                {multiSelectedLayers.length === filteredAndSortedLayers.length && filteredAndSortedLayers.length > 0 ? "None" : "All"}
              </button>

              <button 
                onClick={() => {
                  setIsMultiSelectMode(false);
                  setMultiSelectedLayers([]);
                  setSelectedGroupId("");
                  setIsSavingGroup(false);
                }}
                className="px-2 py-1 bg-fuchsia-600 text-white border border-fuchsia-700 rounded shadow-sm text-xs font-medium hover:bg-fuchsia-700 whitespace-nowrap flex-shrink-0"
              >
                Done
              </button>
            </div>
          )}
          
          {selectedLayers.length > 0 && (
            <div className="flex justify-between items-center px-1 pt-1 text-[11px]">
              <span className="text-indigo-600 font-semibold">{selectedLayers.length} layer{selectedLayers.length !== 1 ? 's' : ''} selected</span>
              <div className="flex gap-4 items-center">
                <button
                  onClick={() => {
                    const willShowOnlySelected = !showOnlySelected;
                    setShowOnlySelected(willShowOnlySelected);
                    if (willShowOnlySelected) {
                      setSearchQuery("");
                    }
                  }}
                  className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors cursor-pointer"
                >
                  {showOnlySelected ? "Show All Layers" : "Show Selected Layers"}
                </button>
                <button 
                  onClick={clearSelectedLayers} 
                  className="text-slate-500 hover:text-slate-800 underline transition-colors cursor-pointer"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Layer List */}
        <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
          {filteredAndSortedLayers.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">No layers found.</div>
          ) : (
            <div className="p-2 space-y-1 relative">
              {filteredAndSortedLayers.map((layer) => {
                const isOn = layer.state === "On";
                const hasCustom = layer.hasCustomStyle;
                let displayColor = layer.color || '#cccccc';
                if (!displayColor.startsWith('#')) displayColor = `#${displayColor}`;

                return (
                  <div
                    key={layer.id}
                    className={`flex items-center gap-2.5 py-1 px-2 rounded-md transition-all ${
                      hasCustom ? "bg-amber-100" : (isOn ? "bg-amber-300/80" : "bg-slate-100")
                    } hover:brightness-95`}
                  >
                    {/* Multiselect Checkbox */}
                    {isMultiSelectMode && (
                      <input
                        type="checkbox"
                        checked={multiSelectedLayers.includes(layer.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMultiSelectedLayers(prev => [...prev, layer.id]);
                          } else {
                            setMultiSelectedLayers(prev => prev.filter(id => id !== layer.id));
                          }
                        }}
                        className="w-4 h-4 flex-shrink-0 text-fuchsia-600 border-gray-300 rounded focus:ring-fuchsia-500 cursor-pointer mr-1"
                        title="Select for batch styling"
                      />
                    )}

                    {/* Custom Style Checkbox */}
                    <input
                      type="checkbox"
                      checked={hasCustom}
                      onChange={() => toggleCustomStyle(layer.id)}
                      className="w-3.5 h-3.5 flex-shrink-0 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                      title="Custom lineweight/color applied"
                    />

                    {/* Lineweight Icon (Opens Slider) */}
                    <button
                      onClick={() => {
                        setActiveColorLayer(null);
                        setActiveSliderLayer(activeSliderLayer === layer.id ? null : layer.id);
                      }}
                      className="p-0.5 rounded text-slate-600 hover:text-indigo-600 hover:bg-white/50 transition-colors"
                      title="Adjust Lineweight"
                    >
                      <img src="/assets/cadviewer/app/images/singleIcons/svg/light-skin/adjustlayerlineweight.svg" alt="" className="w-3.5 h-3.5 opacity-80 flex-shrink-0" />
                    </button>

                    {/* Color Icon & Square (Opens Color Popover) */}
                    <div className="flex items-center gap-1.5 relative">
                      <button
                        onClick={() => {
                           setActiveSliderLayer(null);
                           setTempColor(displayColor);
                           setActiveColorLayer(activeColorLayer === layer.id ? null : layer.id);
                        }}
                        className="p-0.5 rounded text-slate-600 hover:text-indigo-600 hover:bg-white/50 transition-colors"
                        title="Edit Color"
                      >
                        <img src="/assets/cadviewer/app/images/singleIcons/svg/light-skin/layercolor.svg" alt="" className="w-3.5 h-3.5 opacity-80 flex-shrink-0" />
                      </button>
                      
                      <div 
                        className="w-3.5 h-3.5 rounded-sm border border-slate-300 shadow-sm cursor-pointer flex-shrink-0"
                        style={{ backgroundColor: displayColor }}
                        onClick={() => {
                           setActiveSliderLayer(null);
                           setTempColor(displayColor);
                           setActiveColorLayer(activeColorLayer === layer.id ? null : layer.id);
                        }}
                        title="Click to change color"
                      />
                    </div>

                    {/* Layer toggle switch */}
                    <button
                      onClick={() => handleToggleLayer(layer.id, (layer as any).originalId, layer.state)}
                      className={`relative inline-flex flex-shrink-0 h-4 w-7 items-center rounded-full transition-colors duration-200 ease-in-out border border-transparent ${
                        isOn ? "bg-emerald-400 shadow-sm" : "bg-slate-300"
                      }`}
                      title={`Toggle ${layer.name}`}
                    >
                      <span className="sr-only">Toggle layer</span>
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out shadow-sm ${
                          isOn ? "translate-x-[14px]" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                    
                    <span className="text-[11px] font-semibold tracking-tight text-slate-800 truncate" title={layer.name}>
                      {layer.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Global Pop-ups (Rendered outside overflow container) */}
        {activeSliderLayer && (() => {
            const layer = layers.find(l => l.id === activeSliderLayer);
            if (!layer) return null;
            return (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100] bg-white border border-slate-300 shadow-2xl rounded-lg p-4 w-[280px]">
                   <div className="flex justify-between items-center mb-3 border-b border-slate-100 pb-2">
                       <span className="text-sm font-bold text-slate-800">Adjust Line Weights</span>
                       <button onClick={() => setActiveSliderLayer(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500 hover:text-slate-700"/></button>
                   </div>
                   <div className="mb-2 text-xs text-slate-500 font-medium truncate" title={layer.name}>Layer: {layer.name}</div>
                   <div className="flex items-center gap-3">
                       <input 
                           type="range" 
                           min="1" 
                           max="100" 
                           step="1" 
                           value={layer.scaleFactor * 10}
                           onChange={(e) => handleLineweightChange(layer.id, layer.name, parseInt(e.target.value))}
                           className="flex-1 cursor-pointer accent-indigo-600"
                       />
                       <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded border border-slate-200 font-semibold">{layer.scaleFactor.toFixed(2)}</span>
                   </div>
                </div>
            );
        })()}

        {activeColorLayer && (() => {
            const layer = layers.find(l => l.id === activeColorLayer);
            if (!layer) return null;
            
            const isValidHex = (hex: string) => /^#[0-9A-Fa-f]{6}$/i.test(hex);
            
            return (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100] bg-white border border-slate-300 shadow-2xl rounded-lg p-4 w-[280px]">
                   <div className="flex justify-between items-center mb-3 border-b border-slate-100 pb-2">
                       <span className="text-sm font-bold text-slate-800">Select Color (HEX)</span>
                       <button onClick={() => setActiveColorLayer(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500 hover:text-slate-700"/></button>
                   </div>
                   <div className="mb-3 text-xs text-slate-500 font-medium truncate" title={layer.name}>Layer: {layer.name}</div>
                   <div className="flex flex-col gap-4">
                       <input 
                           type="color" 
                           value={isValidHex(tempColor) ? tempColor : '#cccccc'}
                           onChange={(e) => setTempColor(e.target.value)}
                           className="w-full h-12 cursor-pointer rounded border border-slate-200"
                       />
                       <div className="flex justify-between items-center">
                           <input 
                               type="text"
                               value={tempColor}
                               onChange={(e) => {
                                   let val = e.target.value;
                                   if (val.length > 0 && !val.startsWith('#')) val = '#' + val;
                                   setTempColor(val);
                               }}
                               maxLength={7}
                               className="w-24 px-2 py-1.5 text-sm font-mono text-slate-700 uppercase border border-slate-300 rounded focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                           />
                           <div className="flex gap-2">
                               <button onClick={() => setActiveColorLayer(null)} className="px-4 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 transition-colors">Cancel</button>
                               <button onClick={() => {
                                   const finalColor = isValidHex(tempColor) ? tempColor : '#cccccc';
                                   handleColorChange(layer.id, layer.name, finalColor);
                                   setActiveColorLayer(null);
                               }} className="px-4 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">OK</button>
                           </div>
                       </div>
                   </div>
                </div>
            );
        })()}

        {multiActiveSlider && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100] bg-white border border-slate-300 shadow-2xl rounded-lg p-4 w-[280px]">
               <div className="flex justify-between items-center mb-3 border-b border-slate-100 pb-2">
                   <span className="text-sm font-bold text-slate-800">Batch Adjust Line Weights</span>
                   <button onClick={() => setMultiActiveSlider(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500 hover:text-slate-700"/></button>
               </div>
               <div className="mb-2 text-xs text-slate-500 font-medium truncate">Applying to {multiSelectedLayers.length} layers</div>
               <div className="flex items-center gap-3">
                   <input 
                       type="range" 
                       min="1" 
                       max="100" 
                       step="1" 
                       value={multiScaleFactor * 10}
                       onChange={(e) => {
                           const val = parseInt(e.target.value);
                           setMultiScaleFactor(val / 10);
                           handleMultiLineweightChange(val);
                       }}
                       className="flex-1 cursor-pointer accent-indigo-600"
                   />
                   <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded border border-slate-200 font-semibold">{multiScaleFactor.toFixed(2)}</span>
               </div>
            </div>
        )}

        {multiActiveColor && (() => {
            const isValidHex = (hex: string) => /^#[0-9A-Fa-f]{6}$/i.test(hex);
            
            return (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100] bg-white border border-slate-300 shadow-2xl rounded-lg p-4 w-[280px]">
                   <div className="flex justify-between items-center mb-3 border-b border-slate-100 pb-2">
                       <span className="text-sm font-bold text-slate-800">Batch Select Color</span>
                       <button onClick={() => setMultiActiveColor(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500 hover:text-slate-700"/></button>
                   </div>
                   <div className="mb-3 text-xs text-slate-500 font-medium truncate">Applying to {multiSelectedLayers.length} layers</div>
                   <div className="flex flex-col gap-4">
                       <input 
                           type="color" 
                           value={isValidHex(multiTempColor) ? multiTempColor : '#cccccc'}
                           onChange={(e) => setMultiTempColor(e.target.value)}
                           className="w-full h-12 cursor-pointer rounded border border-slate-200"
                       />
                       <div className="flex justify-between items-center">
                           <input 
                               type="text"
                               value={multiTempColor}
                               onChange={(e) => {
                                   let val = e.target.value;
                                   if (val.length > 0 && !val.startsWith('#')) val = '#' + val;
                                   setMultiTempColor(val);
                               }}
                               maxLength={7}
                               className="w-24 px-2 py-1.5 text-sm font-mono text-slate-700 uppercase border border-slate-300 rounded focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                           />
                           <div className="flex gap-2">
                               <button onClick={() => setMultiActiveColor(false)} className="px-4 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 transition-colors">Cancel</button>
                               <button onClick={() => {
                                   const finalColor = isValidHex(multiTempColor) ? multiTempColor : '#cccccc';
                                   handleMultiColorChange(finalColor);
                                   setMultiActiveColor(false);
                               }} className="px-4 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">OK</button>
                           </div>
                       </div>
                   </div>
                </div>
            );
        })()}

        {/* Bottom Actions */}
        <div className="p-3 border-t border-slate-200 flex gap-2">
          <button onClick={handleAllLayersOn} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            All Layer On
          </button>
          <button onClick={handleAllLayersOff} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            All Layer Off
          </button>
          <button onClick={handleSwapLayers} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            Swap Layers
          </button>
        </div>

        {/* Additional Controls */}
        <div className="px-3 pb-3 flex items-center border-t border-slate-100 pt-2">
          <div className="flex items-center gap-2 pr-3">
            <span className="text-xs font-medium text-slate-700">Include Frozen Layers</span>
            <button
              onClick={() => setIncludeFrozenLayers(!includeFrozenLayers)}
              className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                includeFrozenLayers ? "bg-emerald-400" : "bg-slate-200"
              }`}
              title="Toggle Frozen Layers"
            >
              <span className="sr-only">Toggle Frozen Layers</span>
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out shadow-sm ${
                  includeFrozenLayers ? "translate-x-[14px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>

      </motion.div>
    </div>
  );
};

export default SvgStylingPanel;
